import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WeatherWidget.css';

function WeatherWidget({ location, temperatureUnit = 'F' }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDaily, setShowDaily] = useState(false);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!location) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('/api/weather', {
          params: {
            location: location,
            unit: temperatureUnit
          }
        });

        setWeather({
          temp: response.data.temp,
          condition: response.data.condition,
          icon: response.data.icon,
          hourly: response.data.hourly || [],
          daily: response.data.daily || []
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching weather:', error);
        // Fallback to default on error
        setWeather({
          temp: temperatureUnit === 'C' ? 22 : 72,
          condition: 'Unknown',
          icon: '☀️',
          hourly: [],
          daily: []
        });
        setLoading(false);
      }
    };

    fetchWeather();
    
    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, [location, temperatureUnit]);

  const handleClick = () => {
    if (weather?.daily && weather.daily.length > 0) {
      setShowDaily(!showDaily);
    }
  };

  const formatHour = (date) => {
    const d = new Date(date);
    const hour = d.getHours();
    const hour12 = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${hour12}${ampm}`;
  };

  const formatDay = (date) => {
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    }
  };

  if (loading) {
    return (
      <div className="weather-widget">
        <div className="weather-content">Loading...</div>
      </div>
    );
  }

  const displayTemp = weather?.temp || (temperatureUnit === 'C' ? 22 : 72);
  const unitSymbol = temperatureUnit === 'C' ? '°C' : '°F';
  const isClickable = weather?.daily && weather.daily.length > 0;

  return (
    <div 
      className={`weather-widget ${isClickable ? 'clickable' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      title={isClickable ? (showDaily ? 'Click for hourly forecast' : 'Click for 7-day forecast') : ''}
    >
      {!showDaily ? (
        // Hourly forecast view
        <div className="weather-forecast-container">
          <div className="weather-current">
            <span className="weather-icon">{weather?.icon || '☀️'}</span>
            <div className="weather-info">
              <span className="weather-temp">{displayTemp}{unitSymbol}</span>
              {location && <span className="weather-location">{location}</span>}
            </div>
          </div>
          <div className="weather-hourly-forecast">
            {weather?.hourly && weather.hourly.length > 0 ? (
              weather.hourly.slice(0, 7).map((hour, index) => (
                <div key={index} className="weather-hour-item">
                  <div className="weather-hour-time">{formatHour(hour.time)}</div>
                  <div className="weather-hour-icon">{hour.icon}</div>
                  <div className="weather-hour-temp">{hour.temp}{unitSymbol}</div>
                </div>
              ))
            ) : (
              <div className="weather-no-forecast">No hourly forecast available</div>
            )}
          </div>
          {isClickable && (
            <div className="weather-toggle-hint" style={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.4)', marginLeft: '8px', whiteSpace: 'nowrap', alignSelf: 'center' }}>
              Click for 7-day
            </div>
          )}
        </div>
      ) : (
        // Daily forecast view
        <div className="weather-forecast-container">
          <div className="weather-current">
            <span className="weather-icon">{weather?.icon || '☀️'}</span>
            <div className="weather-info">
              <span className="weather-temp">{displayTemp}{unitSymbol}</span>
              {location && <span className="weather-location">{location}</span>}
            </div>
          </div>
          <div className="weather-daily-forecast">
            {weather?.daily && weather.daily.length > 0 ? (
              weather.daily.slice(0, 7).map((day, index) => (
                <div key={index} className="weather-day-item">
                  <div className="weather-day-name">{formatDay(day.date)}</div>
                  <div className="weather-day-icon">{day.icon}</div>
                  <div className="weather-day-temps">
                    <span className="weather-day-high">{day.high}{unitSymbol}</span>
                    <span className="weather-day-low">{day.low}{unitSymbol}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="weather-no-forecast">No daily forecast available</div>
            )}
          </div>
          {isClickable && (
            <div className="weather-toggle-hint" style={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.4)', marginLeft: '8px', whiteSpace: 'nowrap', alignSelf: 'center' }}>
              Click for hourly
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WeatherWidget;

